
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Card, DatePicker, Spin } from "antd";
import axios from "axios";
import moment from "moment";
import urls from "../../api/urls";
import constants from "../../constants/constants";
import { dateFormat_dmy, getUserAndPremiseId } from "../../utils";

const { RangePicker } = DatePicker;

const McnCounts = () => {
  const navigate = useNavigate();
  const [counts, setCounts] = useState({
    booked: 0,
    smileIntransit: 0,
    indiaPostIntransit: 0,
    delivered: 0,
    errorOrders: 0,
    rtoOrders: 0,
  });

  const [dateRange, setDateRange] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const isMounted = React.useRef(false);

  useEffect(() => {
    if (isMounted.current) return;
    isMounted.current = true;

    // Set default date range: last 7 days (including today)
    const toDate = moment();
    const fromDate = moment().subtract(6, "days");
    setDateRange([fromDate, toDate]);

    // Fetch counts for the default range
    fetchMcnCounts(fromDate, toDate);
  }, []);

  const fetchMcnCounts = async (start, end) => {
    try {
      setIsLoading(true);
      if (!start || !end) return;

      const user = getUserAndPremiseId();
      const premiseId = user?.premiseId || "3";
      const token = localStorage.getItem("token");

      const formattedFromDate = start.format("YYYY-MM-DD");
      const formattedToDate = end.format("YYYY-MM-DD");

      const headers = {
        Authorization: `Bearer ${token}`,
      };

      const userinfo = JSON.parse(localStorage.getItem("userinfo"));
      const roles = userinfo?.roles || [];

      if (!roles.includes(constants.ROLES.ROLE_CENTRALOPERATOR)) {
        headers["smcs-premise-id"] = premiseId;
      }

      const response = await axios.get(
        `${urls.GET_MCN_DASHBOARD_STATISTICS}?fromDate=${formattedFromDate}&toDate=${formattedToDate}`,
        { headers }
      );

      if (response.status === 200) {
        setCounts(response.data);
      }
    } catch (error) {
      console.error("Error fetching MCN counts:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateChange = (dates) => {
    setDateRange(dates);
    if (dates && dates.length === 2) {
      fetchMcnCounts(dates[0], dates[1]);
    }
  };

  const cards = [
    {
      count: counts.booked || 0,
      label: "Incoming Shipments",
      bgColor: "#E6F7FF",
      borderColor: "#69C0FF",
      path: "/mcn-in-bound",
      search: "?tab=1",
      clickable: true,
    },
    {
      count: counts.smileIntransit || 0,
      label: "Smile Intransit",
      bgColor: "#F6FFED",
      borderColor: "#95DE64",
      path: "/mcn-in-bound",
      search: "?tab=2",
      clickable: true,
    },
    {
      count: counts.indiaPostIntransit || 0,
      label: "India Post Intransit",
      bgColor: "#FFFBE6",
      borderColor: "#FFEC3D",
      path: "/mcn-in-bound",
      search: "?tab=3",
      clickable: true,
    },
    {
      count: counts.delivered || 0,
      label: "Delivered",
      bgColor: "#F0F5FF",
      borderColor: "#ADC6FF",
      path: "/mcn-in-bound",
      search: "?tab=4",
      clickable: true,
    },
    {
      count: counts.errorOrders || 0,
      label: "Error Orders",
      bgColor: "#FFF2E8",
      borderColor: "#FFBB96",
      path: "/mcn-in-bound",
      search: "?tab=5",
      clickable: true,
    },
    {
      count: counts.rtoOrders || 0,
      label: "RTO Orders",
      bgColor: "#FFF1F0",
      borderColor: "#FF7875",
      path: null,
      search: null,
      clickable: false,
    },
  ];

  return (
    <div style={{ marginTop: "2rem" }}>
      <Card className="filter-card">
        <Row gutter={[16, 16]} style={{ marginBottom: "20px" }}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label" style={{ marginBottom: "5px", fontWeight: "500" }}>Date Range</div>
            <RangePicker
              onChange={handleDateChange}
              value={dateRange}
              format={dateFormat_dmy}
              style={{ width: "100%", borderRadius: "5px" }}
            />
          </Col>
        </Row>
        <Spin spinning={isLoading}>
          <Row gutter={30}>
            {cards.map((card, index) => (
              <Col key={index} xs={24} sm={12} md={8} lg={6} xl={4}>
                <div
                  role={card.clickable ? "button" : undefined}
                  tabIndex={card.clickable ? 0 : undefined}
                  onKeyDown={card.clickable ? (e) => e.key === "Enter" && navigate(card.path + card.search) : undefined}
                  onClick={card.clickable ? () => navigate(card.path + card.search) : undefined}
                  style={{
                    backgroundColor: card.bgColor,
                    border: `1px solid ${card.borderColor}`,
                    borderRadius: "10px",
                    padding: "20px",
                    textAlign: "center",
                    height: "85%",
                    margin: "1.5rem 0",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    cursor: card.clickable ? "pointer" : "default",
                    transition: "opacity 0.2s",
                  }}
                  onMouseEnter={card.clickable ? (e) => { e.currentTarget.style.opacity = "0.9"; } : undefined}
                  onMouseLeave={card.clickable ? (e) => { e.currentTarget.style.opacity = "1"; } : undefined}
                >
                  <div
                    style={{
                      fontSize: "24px",
                      fontWeight: "600",
                      color: "#262626",
                      marginBottom: "4px",
                    }}
                  >
                    {card.count}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#595959",
                      fontWeight: "500",
                    }}
                  >
                    {card.label}
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Spin>
      </Card>
    </div >
  );
};

export default McnCounts;
